import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { guardarArchivo } from './archivo';
import { sha256Hex } from './huella';
import { consultasKeys } from './use-consultas';
import { apiClient } from '../../lib/api-client';
import type {
  CorreccionAbierta,
  DocumentoSeparacion,
  EnlaceDescarga,
  ResultadoIntegridad,
} from '../../types/consultas';

export const HUELLA_NO_COINCIDE =
  'El archivo recibido no coincide con el emitido (huella distinta). No se entregó; intenta de nuevo o verifica la integridad.';

export function documentosKey(separacionId: string) {
  return ['documentos', separacionId] as const;
}

/** Historial de documentos de una separación (`GET /documents?separacionId`). */
export function useDocuments(separacionId: string, enabled: boolean) {
  return useQuery<DocumentoSeparacion[]>({
    queryKey: documentosKey(separacionId),
    queryFn: () => apiClient.documents.list(separacionId),
    enabled: enabled && Boolean(separacionId),
  });
}

export interface DescargaVerificada {
  nombreArchivo: string;
  /** `navegador`: SHA-256 calculado aquí e igual a la huella; `servidor`: solo la verificó el back. */
  verificacion: 'navegador' | 'servidor';
}

/**
 * Descarga una versión emitida y la entrega solo si su SHA-256 coincide con la huella registrada.
 * Nunca genera: un error se reintenta sin crear documentos nuevos (CA-2).
 */
export function useDownloadVersion() {
  return useMutation<DescargaVerificada, Error, { versionId: string; huellaEsperada?: string | null }>({
    mutationFn: async ({ versionId, huellaEsperada }) => {
      const archivo = await apiClient.documents.download(versionId);
      const esperada = (archivo.huella ?? huellaEsperada ?? '').toLowerCase();
      const calculada = await sha256Hex(archivo.blob);
      if (calculada && esperada && calculada !== esperada) {
        throw new Error(HUELLA_NO_COINCIDE);
      }
      guardarArchivo(archivo.blob, archivo.nombreArchivo);
      return {
        nombreArchivo: archivo.nombreArchivo,
        verificacion: calculada && esperada ? 'navegador' : 'servidor',
      };
    },
  });
}

export function useVersionLink() {
  return useMutation<EnlaceDescarga, Error, string>({
    mutationFn: (versionId) => apiClient.documents.link(versionId),
  });
}

export function useVersionIntegrity() {
  return useMutation<ResultadoIntegridad, Error, string>({
    mutationFn: (versionId) => apiClient.documents.integrity(versionId),
  });
}

/** Abre la corrección (`GENERADA → CORREGIDA`) y refresca consultas y documentos. */
export function useOpenCorrection(separacionId: string) {
  const queryClient = useQueryClient();
  return useMutation<CorreccionAbierta, Error, string>({
    mutationFn: (documentoId) => apiClient.documents.correct(documentoId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: consultasKeys.todas }),
        queryClient.invalidateQueries({ queryKey: documentosKey(separacionId) }),
      ]);
    },
  });
}
